import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { generateQuotePDF } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  SignOut,
  Users,
  CurrencyDollar,
  Trophy,
  FilePdf,
  Trash,
  FloppyDisk,
  ChartLineUp,
  Gear,
  Funnel,
  Pulse,
  LinkSimple,
  PaperPlaneTilt,
} from "@phosphor-icons/react";

const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const statusColor = {
  new: "text-blue-600",
  contacted: "text-amber-600",
  quoted: "text-purple-600",
  won: "text-emerald-600",
  lost: "text-zinc-400",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/leads").then((r) => setLeads(r.data));
    api.get("/admin/settings").then((r) => setSettings(r.data));
    api.get("/admin/analytics").then((r) => setAnalytics(r.data));
  };
  useEffect(load, []);

  const refreshLeads = () => {
    api.get("/admin/leads").then((r) => setLeads(r.data));
    api.get("/admin/stats").then((r) => setStats(r.data));
  };

  const refreshAnalytics = () => api.get("/admin/analytics").then((r) => setAnalytics(r.data));

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    api.get("/admin/stats").then((r) => setStats(r.data));
  };

  const removeLead = async (id) => {
    await api.delete(`/admin/leads/${id}`);
    setLeads((prev) => prev.filter((l) => l.id !== id));
    api.get("/admin/stats").then((r) => setStats(r.data));
    toast.success("Lead deleted");
  };

  const pdfFor = async (lead) => {
    await generateQuotePDF({
      customer: { name: lead.name, email: lead.email, phone: lead.phone },
      address: lead.address,
      estimate: lead.estimate,
    });
  };

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", settings);
      toast.success("Pricing saved");
    } catch {
      toast.error("Failed to save pricing");
    }
  };

  const setGlobal = (k, v) => setSettings((s) => ({ ...s, [k]: parseFloat(v) || 0 }));
  const setWebhookUrl = (v) => setSettings((s) => ({ ...s, lead_webhook_url: v }));

  const testWebhook = async () => {
    try {
      await api.put("/admin/settings", settings);
      const { data } = await api.post("/admin/webhook-test");
      if (data.ok) toast.success(`Webhook delivered (HTTP ${data.status_code})`);
      else toast.error(`Endpoint returned HTTP ${data.status_code}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Webhook test failed");
    }
  };
  const setMat = (idx, k, v) =>
    setSettings((s) => {
      const materials = [...s.materials];
      materials[idx] = { ...materials[idx], [k]: parseFloat(v) || 0 };
      return { ...s, materials };
    });

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-60 border-r border-zinc-200 bg-white p-5 sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <img src={LOGO_URL} alt="logo" className="h-10 w-10 object-contain" />
          <div className="leading-none">
            <p className="font-chivo font-black tracking-tight text-zinc-900">ROOF FIX</p>
            <p className="text-[9px] tracking-[0.3em] text-[#EF233C] font-bold">HOUSTON</p>
          </div>
        </Link>
        <p className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-3">Control Room</p>
        <nav className="space-y-1 text-sm">
          <Link to="/" className="block px-3 py-2 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors">
            ← Public Estimator
          </Link>
        </nav>
        <div className="mt-auto">
          <p className="text-xs text-zinc-500 mb-2 truncate">{user?.email}</p>
          <Button data-testid="logout-button" onClick={doLogout} variant="outline" className="w-full rounded-none border-zinc-300 bg-white hover:bg-zinc-100 text-zinc-900">
            <SignOut weight="bold" size={16} className="mr-2" /> Sign Out
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-5 sm:p-8 max-w-full overflow-x-hidden">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-chivo text-3xl font-black tracking-tight text-zinc-900">Dashboard</h1>
              {stats?.today_leads > 0 && (
                <span data-testid="today-leads-badge" className="inline-flex items-center gap-1.5 bg-[#EF233C] text-white text-xs font-bold tracking-wide px-3 py-1 rounded-full animate-pulse">
                  <span className="h-1.5 w-1.5 rounded-full bg-white" />
                  {stats.today_leads} NEW {stats.today_leads === 1 ? "LEAD" : "LEADS"} TODAY
                </span>
              )}
            </div>
            <p className="text-zinc-500 text-sm">Leads & pricing management</p>
          </div>
          <Button onClick={doLogout} className="md:hidden rounded-none bg-white border border-zinc-300 text-zinc-900 hover:bg-zinc-100">
            <SignOut size={16} />
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={<Users weight="duotone" size={22} />} label="Total Leads" value={stats?.total_leads ?? "—"} testid="stat-total-leads" />
          <StatCard icon={<ChartLineUp weight="duotone" size={22} />} label="New Leads" value={stats?.new_leads ?? "—"} testid="stat-new-leads" />
          <StatCard icon={<CurrencyDollar weight="duotone" size={22} />} label="Pipeline Value" value={stats ? money(stats.pipeline_value) : "—"} testid="stat-pipeline" />
          <StatCard icon={<Trophy weight="duotone" size={22} />} label="Won Value" value={stats ? money(stats.won_value) : "—"} testid="stat-won" />
        </div>

        <Tabs defaultValue="leads" onValueChange={(v) => { if (v === "leads") refreshLeads(); if (v === "analytics") refreshAnalytics(); }}>
          <TabsList className="bg-white border border-zinc-200 rounded-none p-0 h-auto">
            <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-none text-zinc-600 data-[state=active]:bg-[#EF233C] data-[state=active]:text-white px-5 py-2.5">
              <Users weight="bold" size={16} className="mr-2" /> Leads
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="rounded-none text-zinc-600 data-[state=active]:bg-[#EF233C] data-[state=active]:text-white px-5 py-2.5">
              <Funnel weight="bold" size={16} className="mr-2" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing" className="rounded-none text-zinc-600 data-[state=active]:bg-[#EF233C] data-[state=active]:text-white px-5 py-2.5">
              <Gear weight="bold" size={16} className="mr-2" /> Pricing
            </TabsTrigger>
          </TabsList>

          {/* Leads */}
          <TabsContent value="leads" className="mt-5">
            <div className="border border-zinc-200 bg-white overflow-x-auto shadow-sm" data-testid="leads-table">
              <table className="w-full text-sm min-w-[760px]">
                <thead>
                  <tr className="text-left text-xs tracking-wider uppercase text-zinc-500 border-b border-zinc-200 bg-zinc-50">
                    <th className="px-4 py-3 font-bold">Customer</th>
                    <th className="px-4 py-3 font-bold">Property</th>
                    <th className="px-4 py-3 font-bold">Material</th>
                    <th className="px-4 py-3 font-bold text-right">Total</th>
                    <th className="px-4 py-3 font-bold">Status</th>
                    <th className="px-4 py-3 font-bold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-zinc-500">
                        No leads yet. Estimates submitted on the public site appear here.
                      </td>
                    </tr>
                  )}
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-zinc-900">{l.name}</p>
                        <p className="text-xs text-zinc-500">{l.email}</p>
                        <p className="text-xs text-zinc-500">{l.phone}</p>
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        <p className="text-xs text-zinc-500 truncate">{l.address}</p>
                      </td>
                      <td className="px-4 py-3 text-zinc-700">{l.estimate?.material_name}</td>
                      <td className="px-4 py-3 text-right font-barlow text-lg font-bold text-zinc-900">{money(l.estimate?.total)}</td>
                      <td className="px-4 py-3">
                        <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                          <SelectTrigger data-testid={`status-select-${l.id}`} className={`w-32 h-9 bg-white border-zinc-300 rounded-none font-semibold ${statusColor[l.status]}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-zinc-200 text-zinc-900 rounded-none">
                            {STATUSES.map((s) => (
                              <SelectItem key={s} value={s} className="capitalize focus:bg-zinc-100">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button onClick={() => pdfFor(l)} size="sm" className="h-9 rounded-none bg-zinc-100 hover:bg-zinc-200 text-zinc-700" data-testid={`lead-pdf-${l.id}`}>
                            <FilePdf weight="bold" size={16} />
                          </Button>
                          <Button onClick={() => removeLead(l.id)} size="sm" className="h-9 rounded-none bg-zinc-100 hover:bg-red-600 hover:text-white text-zinc-700" data-testid={`lead-delete-${l.id}`}>
                            <Trash weight="bold" size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Analytics */}
          <TabsContent value="analytics" className="mt-5">
            {analytics && (
              <div className="space-y-6" data-testid="analytics-panel">
                {/* Funnel */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FunnelCard label="Address Searches" value={analytics.funnel.searches} sub="top of funnel" testid="funnel-searches" />
                  <FunnelCard label="Estimates Generated" value={analytics.funnel.estimates} sub={`${analytics.funnel.search_to_estimate}% of searches`} testid="funnel-estimates" />
                  <FunnelCard label="Leads Captured" value={analytics.funnel.leads} sub={`${analytics.funnel.estimate_to_lead}% of estimates`} testid="funnel-leads" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <ConvCard label="Search → Estimate" value={`${analytics.funnel.search_to_estimate}%`} />
                  <ConvCard label="Estimate → Lead" value={`${analytics.funnel.estimate_to_lead}%`} />
                  <ConvCard label="Search → Lead (overall)" value={`${analytics.funnel.search_to_lead}%`} highlight />
                </div>

                {/* Recent events */}
                <div className="border border-zinc-200 bg-white shadow-sm">
                  <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center gap-2">
                    <Pulse weight="bold" size={16} className="text-[#EF233C]" />
                    <h3 className="font-chivo font-bold tracking-tight text-zinc-900">Recent Activity</h3>
                  </div>
                  <div className="divide-y divide-zinc-100" data-testid="recent-events">
                    {analytics.recent.length === 0 && (
                      <p className="px-4 py-8 text-center text-zinc-500 text-sm">No activity yet.</p>
                    )}
                    {analytics.recent.map((e) => (
                      <div key={e.id} className="px-4 py-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <EventBadge type={e.type} />
                          <span className="text-sm text-zinc-600 truncate">
                            {e.meta?.address || e.meta?.material || (e.meta?.total ? `$${Number(e.meta.total).toLocaleString()}` : "—")}
                          </span>
                        </div>
                        <span className="text-xs text-zinc-400 whitespace-nowrap">
                          {new Date(e.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Pricing */}
          <TabsContent value="pricing" className="mt-5">
            {settings && (
              <div className="space-y-6" data-testid="pricing-editor">
                <div className="border border-zinc-200 bg-white shadow-sm">
                  <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
                    <h3 className="font-chivo font-bold tracking-tight text-zinc-900">Materials ($ per square)</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    {settings.materials.map((m, i) => (
                      <div key={m.id} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_140px] gap-3 items-center border-b border-zinc-100 pb-3">
                        <div>
                          <p className="font-semibold text-sm text-zinc-900">{m.name}</p>
                          <p className="text-xs text-zinc-500 capitalize">{m.category}</p>
                        </div>
                        <PriceField label="Material/sq" value={m.material_per_square} onChange={(v) => setMat(i, "material_per_square", v)} testid={`mat-material-${m.id}`} />
                        <PriceField label="Labor/sq" value={m.labor_per_square} onChange={(v) => setMat(i, "labor_per_square", v)} testid={`mat-labor-${m.id}`} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border border-zinc-200 bg-white shadow-sm">
                  <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
                    <h3 className="font-chivo font-bold tracking-tight text-zinc-900">Global Rates</h3>
                  </div>
                  <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <PriceField label="Waste %" value={settings.waste_pct} onChange={(v) => setGlobal("waste_pct", v)} testid="g-waste" />
                    <PriceField label="Tear-off/sq" value={settings.tearoff_per_square} onChange={(v) => setGlobal("tearoff_per_square", v)} testid="g-tearoff" />
                    <PriceField label="Disposal flat" value={settings.disposal_flat} onChange={(v) => setGlobal("disposal_flat", v)} testid="g-disposal" />
                    <PriceField label="Permit flat" value={settings.permit_flat} onChange={(v) => setGlobal("permit_flat", v)} testid="g-permit" />
                    <PriceField label="Overhead %" value={settings.overhead_pct} onChange={(v) => setGlobal("overhead_pct", v)} testid="g-overhead" />
                    <PriceField label="Profit %" value={settings.profit_pct} onChange={(v) => setGlobal("profit_pct", v)} testid="g-profit" />
                  </div>
                </div>

                <div className="border border-zinc-200 bg-white shadow-sm">
                  <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center gap-2">
                    <LinkSimple weight="bold" size={16} className="text-[#EF233C]" />
                    <h3 className="font-chivo font-bold tracking-tight text-zinc-900">Lead Integration (Zapier / Go High Level)</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      Paste a webhook URL to auto-send every new lead (name, phone, email, address, estimate) to Go High Level or Zapier.
                      In GHL use a Workflow → <span className="font-semibold">Inbound Webhook</span> trigger; in Zapier use a{" "}
                      <span className="font-semibold">Catch Hook</span> trigger. Click Test after saving to send a sample lead.
                    </p>
                    <div>
                      <label className="text-[10px] tracking-wider uppercase text-zinc-500 font-bold">Lead Webhook URL</label>
                      <Input
                        data-testid="webhook-url-input"
                        type="url"
                        value={settings.lead_webhook_url || ""}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                        placeholder="https://hooks.zapier.com/hooks/catch/..."
                        className="mt-1 bg-white border-zinc-300 rounded-none focus-visible:ring-red-500 h-10 text-zinc-900"
                      />
                    </div>
                    <Button
                      onClick={testWebhook}
                      data-testid="test-webhook-button"
                      variant="outline"
                      className="rounded-none border-zinc-300 bg-white hover:bg-zinc-100 text-zinc-900 font-bold tracking-wide h-10"
                    >
                      <PaperPlaneTilt weight="bold" size={16} className="mr-2" /> Save &amp; Test Webhook
                    </Button>
                  </div>
                </div>

                <Button onClick={saveSettings} data-testid="save-pricing-button" className="rounded-none bg-[#EF233C] hover:bg-[#D90429] text-white font-bold tracking-wide h-11 px-8">
                  <FloppyDisk weight="bold" size={18} className="mr-2" /> Save Pricing
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div className="border border-zinc-200 bg-white p-5 shadow-sm hover:border-red-300 transition-all duration-200">
      <div className="text-[#EF233C] mb-3">{icon}</div>
      <p className="text-xs tracking-wider uppercase text-zinc-500 font-bold">{label}</p>
      <p data-testid={testid} className="font-barlow text-3xl font-black tracking-tight mt-1 text-zinc-900">{value}</p>
    </div>
  );
}

function PriceField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] tracking-wider uppercase text-zinc-500 font-bold">{label}</label>
      <Input
        data-testid={testid}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 bg-white border-zinc-300 rounded-none focus-visible:ring-red-500 h-10 text-zinc-900"
      />
    </div>
  );
}

function FunnelCard({ label, value, sub, testid }) {
  return (
    <div className="border border-zinc-200 bg-white p-5 shadow-sm">
      <p className="text-xs tracking-wider uppercase text-zinc-500 font-bold">{label}</p>
      <p data-testid={testid} className="font-barlow text-4xl font-black tracking-tight mt-1 text-zinc-900">{value}</p>
      <p className="text-xs text-zinc-400 mt-1">{sub}</p>
    </div>
  );
}

function ConvCard({ label, value, highlight }) {
  return (
    <div className={`border p-4 ${highlight ? "border-[#EF233C] bg-red-50" : "border-zinc-200 bg-white"}`}>
      <p className="text-xs tracking-wider uppercase text-zinc-500 font-bold">{label}</p>
      <p className={`font-barlow text-3xl font-black tracking-tight mt-1 ${highlight ? "text-[#EF233C]" : "text-zinc-900"}`}>{value}</p>
    </div>
  );
}

const EVENT_STYLES = {
  search: "bg-blue-100 text-blue-700",
  estimate: "bg-amber-100 text-amber-700",
  lead: "bg-emerald-100 text-emerald-700",
};

function EventBadge({ type }) {
  const cls = EVENT_STYLES[type] || "bg-zinc-100 text-zinc-600";
  return (
    <span className={`text-[10px] tracking-wider uppercase font-bold px-2 py-1 rounded ${cls}`}>{type}</span>
  );
}
